import { Form, Input, Modal, Select, Switch, notification } from 'antd';
import { useEffect, useState } from 'react';
import { formatDate, formatDateTime } from '../utils';
import APIClient from '../api/api';
import { User, UserRole } from '../api/models';
import { Button, Column, DataTable, StatusTag } from '../components/ui';
import { PageHead, PageToolbar } from '../layout';
import { useAuth } from '../auth/AuthContext';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>();
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resettingFor, setResettingFor] = useState<User | null>(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      const resp = await APIClient.listUsers();
      setUsers(resp.users);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load users.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(values: any) {
    try {
      await APIClient.createUser({
        email: values.email.trim(),
        name: values.name.trim(),
        role: values.role,
      });
      notification.success({
        message: `User created. A setup link has been emailed to ${values.email.trim()}.`,
      });
      setCreateOpen(false);
      createForm.resetFields();
      loadUsers();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to create user.',
      });
    }
  }

  async function handleEdit(values: any) {
    if (!editing) return;
    try {
      await APIClient.updateUser(editing.id, {
        name: values.name?.trim(),
        role: values.role,
      });
      notification.success({ message: 'User updated.' });
      setEditing(null);
      editForm.resetFields();
      loadUsers();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to update user.',
      });
    }
  }

  async function handleToggleDisabled(target: User) {
    try {
      await APIClient.updateUser(target.id, { disabled: !target.disabled });
      notification.success({
        message: target.disabled ? 'User enabled.' : 'User disabled.',
      });
      loadUsers();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to update user.',
      });
    }
  }

  async function handleSendResetLink() {
    if (!resettingFor) return;
    try {
      setSendingReset(true);
      await APIClient.sendPasswordReset(resettingFor.id);
      notification.success({
        message: `Password reset link sent to ${resettingFor.email}.`,
      });
      setResettingFor(null);
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to send reset link.',
      });
    } finally {
      setSendingReset(false);
    }
  }

  function openEdit(u: User) {
    setEditing(u);
    editForm.setFieldsValue({ name: u.name, role: u.role });
  }

  const columns: Column<User>[] = [
    {
      key: 'email',
      title: 'Email',
      render: (u) => (
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-text font-medium">{u.email}</span>
          {currentUser?.id === u.id && (
            <span className="text-[10px] uppercase tracking-wider text-text-3">(you)</span>
          )}
        </div>
      ),
    },
    {
      key: 'name',
      title: 'Name',
      width: 180,
      render: (u) => <span className="text-[12.5px] text-text-2">{u.name}</span>,
    },
    {
      key: 'role',
      title: 'Role',
      width: 110,
      render: (u) => (
        <StatusTag tone={u.role === 'admin' ? 'accent' : 'neutral'}>{u.role}</StatusTag>
      ),
    },
    {
      key: 'disabled',
      title: 'Status',
      width: 110,
      render: (u) => (
        <StatusTag tone={u.disabled ? 'danger' : 'success'}>
          {u.disabled ? 'disabled' : 'active'}
        </StatusTag>
      ),
    },
    {
      key: 'lastLoginAt',
      title: 'Last login',
      width: 160,
      render: (u) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      width: 140,
      render: (u) => (
        <span className="font-mono text-[12px] text-text-3 tabular-nums">
          {formatDate(u.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 280,
      render: (u) => {
        const isSelf = currentUser?.id === u.id;
        return (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => openEdit(u)}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setResettingFor(u)}>
              Send reset link
            </Button>
            <Switch
              checked={!u.disabled}
              onChange={() => handleToggleDisabled(u)}
              size="small"
              disabled={isSelf}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHead
        title="Users"
        subtitle="Dashboard accounts and access control."
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Create user
          </Button>
        }
      />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {users ? (
            <>
              total <b className="text-text-2">{users.length}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={users ?? []}
        rowKey={(u) => String(u.id)}
        loading={loading}
        emptyMessage="No users."
      />

      <Modal
        title="Create user"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        okText="Create"
      >
        <Form form={createForm} onFinish={handleCreate} layout="vertical" initialValues={{ role: 'standard' as UserRole }}>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item label="Role" name="role" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Standard', value: 'standard' },
                { label: 'Admin', value: 'admin' },
              ]}
            />
          </Form.Item>
          <div className="text-[12px] text-text-3">
            A link to set their password will be emailed to the user. It is
            valid for 24 hours.
          </div>
        </Form>
      </Modal>

      <Modal
        title={editing ? `Edit ${editing.email}` : 'Edit user'}
        open={!!editing}
        onCancel={() => {
          setEditing(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="Save"
      >
        <Form form={editForm} onFinish={handleEdit} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Role" name="role" rules={[{ required: true }]}>
            <Select
              disabled={editing?.id === currentUser?.id}
              options={[
                { label: 'Standard', value: 'standard' },
                { label: 'Admin', value: 'admin' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={resettingFor ? `Send password reset link` : 'Send password reset link'}
        open={!!resettingFor}
        onCancel={() => setResettingFor(null)}
        onOk={handleSendResetLink}
        okText="Send link"
        confirmLoading={sendingReset}
      >
        {resettingFor && (
          <div className="text-[13px] text-text-2">
            Send a password reset link to <b className="text-text">{resettingFor.email}</b>?
            The link will be valid for 24 hours and can only be used once. Any previously
            issued links for this user will be invalidated.
          </div>
        )}
      </Modal>
    </>
  );
};

export default Users;
